//RPC socket Wrapper
import { RpcSocket } from "./RpcClients/RpcSocket";
import {
  notifyNetworkError,
  notifyNetworkRecovered,
} from "@/utils/networkStatus";

// Helper function to create unique IDs
const createID = (() => {
  let id = 10;
  return () => id++;
})();

export const getId = createID;

// Function to generate JSON-RPC payloads
export function generateRpcRequestPayload({
  id,
  method,
  params = {},
}: {
  id?: number;
  method: string;
  params?: Record<string, any>;
}) {
  return {
    jsonrpc: "2.0",
    id: id || getId(),
    method,
    params,
  };
}

type FetchOptions = {
  path?: string;
  host?: string;
  headers?: Record<string, string>;
  query?: Record<string, any>;
  method?: string;
  body?: any;
  isRpcReq?: boolean;
  // Defaults to "include" below; pass "omit" for hosts (e.g. the asset host)
  // that authenticate via a request header instead of cookies and whose CORS
  // policy doesn't allow credentialed cross-site requests.
  credentials?: RequestCredentials;
  parser?: (response: Response) => Promise<any>;
  retryOptions?: {
    maxRetries?: number;
    retryDelay?: number;
    retryCondition?: (error: any, response?: Response) => boolean;
  };
};

interface RpcResponse {
  code: number;
  error: string;
  result?: any;
}

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export async function RpcHttpClient({
  host,
  query = {},
  body,
  path = "/rpc",
  headers = {},
  method = "POST",
  isRpcReq = true,
  parser = async (response: Response) => {
    const result = await response.json();
    return { code: result.code, error: result.error, result: result.result };
  },
  retryOptions = {},
  ...extraOptions
}: FetchOptions): Promise<RpcResponse> {
  const {
    maxRetries = 5,
    retryDelay = 1000,
    retryCondition = (error: any, _response?: Response) => {
      // Don't retry on 401 or 403 errors
      if (error?.code === 401 || error?.code === 403) {
        console.error("login debug::401 or 403 logging out user", error);
        return false;
      }
      return error?.code === 504 || error?.code === 503;
    },
  } = retryOptions;

  const queryString = Object.keys(query).length
    ? `?${new URLSearchParams(query).toString()}`
    : "";
  const url = `${host}${path}${queryString}`;

  const reqOptions: RequestInit = {
    method,
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
    credentials: "include",
    body: body
      ? isRpcReq
        ? JSON.stringify(generateRpcRequestPayload(body))
        : JSON.stringify(body)
      : undefined,
    ...extraOptions,
  };

  let lastError: any;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await fetch(url, reqOptions);
      notifyNetworkRecovered();

      if (response.status === 401) {
        console.error("login debug::401 logging out user", response);
        // Set flag to prevent further RPC calls
        isRedirectingToLogin = true;

        // Only redirect if not already on login page
        if (window.location.pathname !== "/") {
          window.location.href = "/";
        }
        return { code: 401, error: "Unauthorized" };
      }

      if (response.status === 403) {
        return { code: 403, error: "Unauthorized" };
      }

      try {
        const result = await parser(response);

        if (result.code === 200) {
          return result;
        }

        // Handle 401 in parsed response

        if (attempt < maxRetries && retryCondition(result, response)) {
          const delayMs = retryDelay * Math.pow(2, attempt);
          await delay(delayMs);
          continue;
        }
        return result;
      } catch (parseError) {
        lastError = {
          code: 503,
          error: "Invalid response" + String(parseError),
        };
        if (attempt < maxRetries && retryCondition(lastError, response)) {
          const delayMs = retryDelay * Math.pow(2, attempt);
          await delay(delayMs);
          continue;
        }

        return lastError;
      }
    } catch (fetchError) {
      lastError = { code: 504, error: "request failed" + String(fetchError) };
      notifyNetworkError();

      if (attempt < maxRetries && retryCondition(lastError)) {
        const delayMs = retryDelay * Math.pow(2, attempt);
        await delay(delayMs);
        continue;
      }

      return lastError;
    }
  }
  return lastError || { code: 504, error: "Max retries exceeded" };
}

export interface RequestConfig {
  useSocket: boolean;
  socketInstance?: RpcSocket;
}

export let globalRequestConfig: RequestConfig = {
  useSocket: false,
  socketInstance: undefined,
};

// Global flag to prevent RPC calls when user is being redirected due to 401
let isRedirectingToLogin = false;

export const setRequestMode = (config: Partial<RequestConfig>) => {
  globalRequestConfig = { ...globalRequestConfig, ...config };

  // Expose for debugging
  if (typeof window !== "undefined") {
    (window as any).__socketDebug = {
      getGlobalRequestConfig: () => globalRequestConfig,
      setRequestMode,
      isRedirectingToLogin: () => isRedirectingToLogin,
    };
  }
};

// Function to set redirecting flag
export const setRedirectingToLogin = (redirecting: boolean) => {
  isRedirectingToLogin = redirecting;
};

// Function to reset redirecting flag (call when user successfully logs in)
export const resetRedirectingFlag = () => {
  isRedirectingToLogin = false;
};

// Helper function to make a socket request
const makeSocketRequest = (
  socketInst: RpcSocket,
  method: string,
  params: Record<string, any>
): Promise<RpcResponse> => {
  return new Promise((resolve) => {
    socketInst.sendRequest({ method, params }, ({ result, error }: any) => {
      resolve(
        error
          ? { code: 500, error: String(error), result: undefined }
          : { code: 200, error: "", result }
      );
    });
  });
};

// Helper function to make an HTTP request
const makeHttpRequest = (
  method: string,
  params: Record<string, any>
): Promise<RpcResponse> => {
  // Check if we're redirecting to login due to 401 - prevent new requests
  if (isRedirectingToLogin) {
    return Promise.resolve({ code: 401, error: "Redirecting to login" });
  }

  return RpcHttpClient({
    headers: {},
    host: process.env["NX_APP_API_BACKEND"],
    body: { method, params },
    isRpcReq: true,
    retryOptions: {
      maxRetries: 20,
      retryDelay: 1000,
      retryCondition: (error: any, _response?: Response) => {
        // Don't retry on 401 or 403 errors
        if (error?.code === 401 || error?.code === 403) {
          return false;
        }
        const shouldRetry = error?.code === 503 || error?.code === 504;
        return shouldRetry;
      },
    },
  });
};

// Helper function to wait for socket to be ready
const waitForSocketToBeReady = (
  method: string,
  params: Record<string, any>,
  maxWaitTime: number = 30000
): Promise<{ isReady: boolean; socketInst?: RpcSocket }> => {
  return new Promise((resolve) => {
    const startTime = Date.now();

    const checkSocket = () => {
      const elapsed = Date.now() - startTime;
      const currentSocketInst = globalRequestConfig.socketInstance;

      if (currentSocketInst?.sendRequest) {
        resolve({ isReady: true, socketInst: currentSocketInst });
        return;
      }

      if (elapsed >= maxWaitTime) {
        console.error(
          "⏰ Socket not ready after",
          maxWaitTime,
          "ms for:",
          method
        );
        resolve({ isReady: false });
        return;
      }

      setTimeout(checkSocket, 500);
    };

    checkSocket();
  });
};
const isNetworkFailure = (res: RpcResponse): boolean =>
  res?.code === 504 || res?.code === 503;

const trackNetworkStatus = (res: RpcResponse): RpcResponse => {
  if (isNetworkFailure(res)) {
    notifyNetworkError();
  } else if (res?.code === 200) {
    notifyNetworkRecovered();
  }
  return res;
};

export const sendReqToController = async (
  method: string,
  params: Record<string, any>,
  useSocket?: boolean,
  socketInstance?: RpcSocket
): Promise<RpcResponse> => {
  // Check if we're redirecting to login due to 401 - prevent new requests
  if (isRedirectingToLogin) {
    return { code: 401, error: "Redirecting to login" };
  }

  // If useSocket is false, directly use HTTP
  if (useSocket === false) {
    return trackNetworkStatus(await makeHttpRequest(method, params));
  }

  const socketInst = socketInstance ?? globalRequestConfig.socketInstance;

  if (socketInst?.sendRequest) {
    return trackNetworkStatus(
      await makeSocketRequest(socketInst, method, params)
    );
  }

  // Socket not ready, wait for it
  const { isReady, socketInst: readySocketInst } = await waitForSocketToBeReady(
    method,
    params
  );

  // Use socket if it became ready, otherwise fallback to HTTP
  return trackNetworkStatus(
    isReady && readySocketInst
      ? await makeSocketRequest(readySocketInst, method, params)
      : await makeHttpRequest(method, params)
  );
};
